import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableSortLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";

import APIHelper from "../utils/APIHelper";

export default function InventoryPage({ token }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("sku");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Dialog state for Adjust only
  const [adjustStockOpen, setAdjustStockOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustValue, setAdjustValue] = useState("");

  const fetchProducts = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const res = await APIHelper.listProducts(token, {
        q: query,
        status: statusFilter === "All" ? undefined : statusFilter,
        page: page + 1,
        limit: rowsPerPage,
        sortBy: sortBy === "name" ? "title" : sortBy,
        sortDir,
      });
      const items = res.items || [];
      setProducts(items);
      setTotal(res.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
      setErrorMessage("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, rowsPerPage, sortBy, sortDir, statusFilter, query]);

  const getStatusChip = (status) => {
    return (
      <Chip
        label={status}
        size="small"
        className={`text-xs font-semibold ${
          status === "Active"
            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
        }`}
      />
    );
  };

  const handleSort = (column) => {
    const mapped = column === "name" ? "title" : column;
    if (sortBy === mapped) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(mapped);
      setSortDir("asc");
    }
    setPage(0);
  };

  const handleAdjustStock = (product) => {
    setSelectedProduct(product);
    setAdjustValue("");
    setAdjustStockOpen(true);
  };

  const handleSaveAdjustStock = async () => {
    if (!selectedProduct || !adjustValue) return;
    const adjustment = parseInt(adjustValue, 10);
    if (isNaN(adjustment)) {
      setErrorMessage("Please enter a valid number");
      return;
    }
    try {
      const newStock = (selectedProduct.stock || 0) + adjustment;
      if (newStock < 0) {
        setErrorMessage("Stock cannot be negative");
        return;
      }
      await APIHelper.updateProduct(token, selectedProduct._id, {
        stock: newStock,
      });
      setAdjustStockOpen(false);
      setSelectedProduct(null);
      setAdjustValue("");
      fetchProducts();
    } catch (error) {
      console.error("Error adjusting stock:", error);
      setErrorMessage(
        error?.response?.data?.message || "Failed to adjust stock"
      );
    }
  };

  return (
    <div className="flex flex-col">
      <div className={`flex items-center justify-between ${errorMessage ? 'mb-0' : 'mb-6'}`}>
        <div className="flex items-center gap-2">
          <Inventory2Icon className="text-primary-600 dark:text-primary-400" />
          <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
            Inventory
          </Typography>
        </div>
      </div>

      {errorMessage && (
        <div className="mt-4 mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-red-800 dark:text-red-200">
              ⚠️ {errorMessage}
            </Typography>
            <button
              onClick={() => setErrorMessage("")}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="card-modern p-6 mb-6">
        <Typography variant="body2" className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Manage your products, stock levels, and SKUs here.
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-3">
            <TextField
              fullWidth
              label="Search SKU / Name"
              size="small"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              disabled={loading}
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
          </div>
          <div>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label" className="text-gray-700 dark:text-gray-300">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                disabled={loading}
                className="bg-white dark:bg-gray-800"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      <div className="card-modern p-6">
        <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Items ({total})
        </Typography>
        <div className="overflow-x-auto">
          <TableContainer>
            <Table size="small" className="min-w-[650px]">
              <TableHead>
                <TableRow className="bg-gray-50 dark:bg-gray-800">
                  <TableCell className="text-gray-900 dark:text-white font-semibold">
                    <TableSortLabel
                      active={sortBy === "sku"}
                      direction={sortBy === "sku" ? sortDir : "asc"}
                      onClick={() => handleSort("sku")}
                      className="text-gray-900 dark:text-white"
                    >
                      SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="text-gray-900 dark:text-white font-semibold">
                    <TableSortLabel
                      active={sortBy === "title"}
                      direction={sortBy === "title" ? sortDir : "asc"}
                      onClick={() => handleSort("name")}
                      className="text-gray-900 dark:text-white"
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" className="text-gray-900 dark:text-white font-semibold">
                    <TableSortLabel
                      active={sortBy === "stock"}
                      direction={sortBy === "stock" ? sortDir : "asc"}
                      onClick={() => handleSort("stock")}
                      className="text-gray-900 dark:text-white"
                    >
                      Stock
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" className="text-gray-900 dark:text-white font-semibold">
                    <TableSortLabel
                      active={sortBy === "price"}
                      direction={sortBy === "price" ? sortDir : "asc"}
                      onClick={() => handleSort("price")}
                      className="text-gray-900 dark:text-white"
                    >
                      Price (INR)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="text-gray-900 dark:text-white font-semibold">Status</TableCell>
                  <TableCell align="right" className="text-gray-900 dark:text-white font-semibold">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                        Loading...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                        No products found.
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((item) => (
                    <TableRow key={item._id || item.sku} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <TableCell className="text-gray-900 dark:text-white">{item.sku}</TableCell>
                      <TableCell className="text-gray-900 dark:text-white">{item.title}</TableCell>
                      <TableCell align="right" className="text-gray-900 dark:text-white">
                        {(item.stock || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" className="text-gray-900 dark:text-white">
                        ₹{Number(item.price || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>{getStatusChip(item.status)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleAdjustStock(item)}
                          className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 normal-case"
                        >
                          Adjust
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div className="mt-4">
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => {
              setPage(p);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            className="text-gray-700 dark:text-gray-300"
          />
        </div>
      </div>

      {/* Adjust Stock Dialog */}
      <Dialog
        open={adjustStockOpen}
        onClose={() => {
          setAdjustStockOpen(false);
          setSelectedProduct(null);
          setAdjustValue("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "bg-white dark:bg-gray-900 rounded-2xl shadow-soft-lg border border-gray-100 dark:border-gray-700"
        }}
      >
        <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white pb-3 pt-6 px-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <span className="text-xl">📦</span>
            </div>
            <span>Adjust Stock</span>
          </div>
        </DialogTitle>
        <DialogContent className="px-6 py-6">
          {selectedProduct && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                <Typography variant="body2" className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Product
                </Typography>
                <Typography variant="body2" className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedProduct.title} ({selectedProduct.sku})
                </Typography>
              </div>
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800">
                <Typography variant="body2" className="text-sm font-medium text-primary-900 dark:text-primary-200 mb-1">
                  Current Stock
                </Typography>
                <Typography variant="h6" className="text-2xl font-bold text-primary-700 dark:text-primary-300">
                  {(selectedProduct.stock || 0).toLocaleString()}
                </Typography>
              </div>
              <TextField
                fullWidth
                label="Adjustment Amount"
                type="number"
                value={adjustValue}
                onChange={(e) => setAdjustValue(e.target.value)}
                helperText="Enter positive number to add, negative to subtract (e.g., +10 or -5)"
                className="bg-white dark:bg-gray-800"
                placeholder="e.g., +10 or -5"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.75rem',
                  },
                }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions className="px-6 pb-6 pt-2">
          <Button
            onClick={() => {
              setAdjustStockOpen(false);
              setSelectedProduct(null);
              setAdjustValue("");
            }}
            className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case px-4"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAdjustStock}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            Adjust
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
